import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { IconCalendar, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { format } from "date-fns";
import { useState } from "react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Buckets are plain calendar strings, so they map to local dates by their
// parts; going through Date.parse would shift them by the UTC offset.
function dayToDate(bucket?: string): Date | undefined {
  if (!bucket) return undefined;
  const [y, m, d] = bucket.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
}

function monthOf(bucket: string): string {
  return bucket.slice(0, 7);
}

function formatBucketLabel(period: "month" | "day", bucket: string): string {
  if (period === "day") return format(dayToDate(bucket)!, "MMM d, yyyy");
  const [y, m] = bucket.split("-").map(Number);
  return `${MONTHS[m! - 1]} ${y}`;
}

export function BucketPicker({
  period,
  value,
  onChange,
  min,
  max,
  placeholder,
  ariaLabel,
  className,
}: {
  period: "month" | "day";
  // "" when unset; YYYY-MM for month, YYYY-MM-DD for day.
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder: string;
  ariaLabel: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  function pick(bucket: string) {
    onChange(bucket);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={ariaLabel}
          className={cn("h-9 justify-start gap-2 font-normal", !value && "text-muted-foreground", className)}
        >
          <IconCalendar className="size-4 shrink-0" />
          <span className="truncate">{value ? formatBucketLabel(period, value) : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        {period === "day" ? (
          <Calendar
            mode="single"
            selected={dayToDate(value)}
            onSelect={(d) => d && pick(format(d, "yyyy-MM-dd"))}
            defaultMonth={dayToDate(value) ?? dayToDate(max)}
            startMonth={dayToDate(min)}
            endMonth={dayToDate(max)}
            disabled={[
              ...(min ? [{ before: dayToDate(min)! }] : []),
              ...(max ? [{ after: dayToDate(max)! }] : []),
            ]}
          />
        ) : (
          <MonthGrid value={value} onPick={pick} min={min && monthOf(min)} max={max && monthOf(max)} />
        )}
        {value && (
          <div className="flex justify-end border-t pt-2">
            <Button variant="ghost" size="sm" className="h-9 sm:h-8" onClick={() => pick("")}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function MonthGrid({
  value,
  onPick,
  min,
  max,
}: {
  value: string;
  onPick: (bucket: string) => void;
  min?: string;
  max?: string;
}) {
  const initialYear = Number((value || max || format(new Date(), "yyyy-MM")).slice(0, 4));
  const [year, setYear] = useState(initialYear);
  const minYear = min ? Number(min.slice(0, 4)) : undefined;
  const maxYear = max ? Number(max.slice(0, 4)) : undefined;

  return (
    <div className="flex w-64 flex-col gap-2 p-1">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          className="size-9"
          aria-label="Previous year"
          disabled={minYear !== undefined && year <= minYear}
          onClick={() => setYear((y) => y - 1)}
        >
          <IconChevronLeft className="size-4" />
        </Button>
        <span className="text-sm font-medium tabular-nums">{year}</span>
        <Button
          variant="ghost"
          size="icon"
          className="size-9"
          aria-label="Next year"
          disabled={maxYear !== undefined && year >= maxYear}
          onClick={() => setYear((y) => y + 1)}
        >
          <IconChevronRight className="size-4" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {MONTHS.map((label, i) => {
          const bucket = `${year}-${String(i + 1).padStart(2, "0")}`;
          const outOfRange = (min !== undefined && bucket < min) || (max !== undefined && bucket > max);
          return (
            <Button
              key={bucket}
              variant={bucket === value ? "default" : "ghost"}
              className="h-9 font-normal"
              disabled={outOfRange}
              onClick={() => onPick(bucket)}
            >
              {label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
